/**
 * Filter Chips Component
 * Toggle buttons with badge counts for filtering by tag type
 */

import { memo, useCallback } from 'react'
import { ToggleButton, Badge, makeStyles, tokens, mergeClasses } from '@fluentui/react-components'
import type { IssueTagType } from '../../../domain'
import { FILTER_CHIPS } from '../types/workQueueFilters'
import { useWorkQueueFilter } from '../context'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  chip: {
    color: tokens.colorNeutralForegroundOnBrand,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    border: `1px solid ${tokens.colorNeutralStrokeOnBrand}`,
    ':hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      color: tokens.colorNeutralForegroundOnBrand,
    },
  },
  chipChecked: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    color: tokens.colorBrandBackground,
    ':hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.85)',
      color: tokens.colorBrandBackground,
    },
  },
  badge: {
    marginLeft: tokens.spacingHorizontalXS,
  },
})

export const FilterChips = memo(function FilterChips() {
  const styles = useStyles()
  const { activeTagFilter, setActiveTagFilter, tagCounts } = useWorkQueueFilter()

  const handleChipClick = useCallback(
    (tagType: IssueTagType) => {
      if (activeTagFilter === tagType) {
        setActiveTagFilter(null)
      } else {
        setActiveTagFilter(tagType)
      }
    },
    [activeTagFilter, setActiveTagFilter]
  )

  return (
    <div className={styles.root}>
      {FILTER_CHIPS.map(({ tagType, label }) => {
        const count = tagCounts[tagType] ?? 0
        const isActive = activeTagFilter === tagType

        return (
          <ToggleButton
            key={tagType}
            checked={isActive}
            onClick={() => handleChipClick(tagType)}
            className={mergeClasses(styles.chip, isActive && styles.chipChecked)}
            appearance="outline"
            size="small"
            aria-pressed={isActive}
          >
            {label}
            {count > 0 && (
              <Badge
                appearance={isActive ? 'filled' : 'tint'}
                size="small"
                className={styles.badge}
              >
                {count}
              </Badge>
            )}
          </ToggleButton>
        )
      })}
    </div>
  )
})
